export interface FileMeta {
  name: string;
  path: string;
  /** User-supplied output name (including xwi_prefix). Sent to the API as the destination filename. */
  new_name?: string;
}

export function toFileMeta(path: string): FileMeta {
  return { path, name: path.split('/').pop() ?? path };
}

export interface FileSpec {
  key: string;
  label: string;
  /** Regex string applied to filter compatible files from the source list */
  regex?: string;
  // Regex for selecting the 'default' for this field. if not found, none will be applied
  defaultSelectionRegex?: string;
  required?: boolean;
  /** Which list to draw files from — defaults to 'all_files' */
  source?: 'all_files' | 'runfiles';
  /** ng-zorro icon type — defaults to DEFAULT_FILE_ICON */
  icon?: string;
  /** CSS hex color for the card accent + icon — defaults to DEFAULT_FILE_COLOR */
  color?: string;
  /** Base output filename. At runtime this is prefixed with xwi_prefix to form the default output name. */
  defaultName?: string;
  /**
   * When true, this file is sourced from any job in the project (search a job,
   * pick one of its Vp/CP volumes) rather than from the template job's files.
   */
  sourceFromJob?: boolean;
}

export interface JobTypeConfig {
  label: string;
  files: FileSpec[];
}

export const DEFAULT_FILE_ICON = 'file';
export const DEFAULT_FILE_COLOR = '#8c8c8c';

export const JOB_TYPE_CONFIGS: Record<string, JobTypeConfig> = {
  fwi: {
    label: 'Full Waveform Inversion',
    files: [
      {
        key: 'velocity',
        label: 'Velocity File',
        icon: 'thunderbolt',
        color: '#fa8c16',
        regex: '.*(-StartVp|-Vp)\.sgy$',
        defaultSelectionRegex: ".*-StartVp\.sgy$",
        defaultName: 'StartVp.sgy',
        required: true,
        sourceFromJob: true,
      },
      {
        key: 'runfile',
        label: 'Base Runfile',
        icon: 'file-text',
        color: '#13c2c2',
        source: 'runfiles',
        defaultSelectionRegex: "^(?!.*CP\\d{5}).*-Runfile\\.key$", // uses negative lookahead to skip any CP runfiles, gets only the BASE runfile
        defaultName: 'Runfile.key',
        required: true,
      },
      {
        key: 'trueDelta',
        label: 'True Delta File',
        icon: 'deployment-unit',
        color: '#722ed1',
        defaultSelectionRegex: ".*-TrueDelta\.sgy$",
        defaultName: 'TrueDelta.sgy',
        required: false,
      },
      {
        key: 'trueEpsilon',
        label: 'True Epsilon File',
        icon: 'deployment-unit',
        color: '#722ed1',
        defaultSelectionRegex: ".*-TrueEpsilon\.sgy$",
        defaultName: 'TrueEpsilon.sgy',
        required: false,
      },
    ],
  },
  anisotropy: {
    label: 'Anisotropy Inversion',
    files: [
      {
        key: 'velocity',
        label: 'Velocity File',
        icon: 'thunderbolt',
        color: '#fa8c16',
        regex: '.*-Vp\.sgy$',
        defaultName: 'StartVp.sgy',
        required: true,
        sourceFromJob: true,
      },
      {
        key: 'anisotropy',
        label: 'Anisotropy File',
        icon: 'deployment-unit',
        color: '#722ed1',
        regex: '.*\\.(sgy|vtr)$',
        defaultName: 'Anisotropy.sgy',
        required: true,
      },
      {
        key: 'runfile',
        label: 'Base Runfile',
        icon: 'file-text',
        color: '#13c2c2',
        source: 'runfiles',
        defaultSelectionRegex: "^(?!.*CP\\d{5}).*-Runfile\\.key", // uses negative lookahead to skip any CP runfiles, gets only the BASE runfile
        defaultName: 'Runfile.key',
        required: true,
      },
    ],
  },
  rtm: {
    label: 'Reverse Time Migration',
    files: [
      {
        key: 'velocity',
        label: 'Velocity File',
        icon: 'thunderbolt',
        color: '#fa8c16',
        regex: '.*-Vp\.sgy$',
        defaultName: 'StartVp.sgy',
        required: true,
        sourceFromJob: true,
      },
      {
        key: 'runfile',
        label: 'Base Runfile',
        icon: 'file-text',
        color: '#13c2c2',
        source: 'runfiles',
        defaultSelectionRegex: "^(?!.*CP\\d{5}).*-Runfile\\.key", // uses negative lookahead to skip any CP runfiles, gets only the BASE runfile
        defaultName: 'Runfile.key',
        required: true,
      },
    ],
  },
};

export const JOB_TYPE_OPTIONS = Object.entries(JOB_TYPE_CONFIGS).map(([value, config]) => ({
  value,
  label: config.label,
}));
